<template>
  <TransitionCollapse>
    <div
      v-if="isVisible"
      id="notice-very-top"
      class="notice-on-top relative"
      :class="notice.color"
    >
      <div class="container">
        <div class="c-py-2 text-white">
          <div class="row no-gutters c-xl:c-items-center c-items-start">
            <!-- image or icon + text -->
            <div class="col">
              <!-- text -->
              <div class="row c-m-0 c-items-center c-xl:c-justify-center">
                <!-- text -->
                <div
                  v-if="countdownObject && notice.showTimer === true"
                  class="flex c-items-center text-primary countdown c-mr-4 c-lg:c-mb-0 c-mb-2"
                >
                  <div class="item bg-white c-p-1 c-rounded-sm flex c-flex-col c-items-center c-mr-1">
                    <strong class="text-danger">{{ countdownObject.days < 10 ? '0' : '' }}{{ countdownObject.days }}</strong>
                    <span>{{ t('days') }}</span>
                  </div>
                  <div class="item bg-white c-p-1 c-rounded-sm flex c-flex-col c-items-center c-mr-1">
                    <strong class="text-danger">{{ countdownObject.hours < 10 ? '0' : '' }}{{ countdownObject.hours }}</strong>
                    <span>{{ t('hours') }}</span>
                  </div>
                  <div class="item bg-white c-p-1 c-rounded-sm flex c-flex-col c-items-center c-mr-1">
                    <strong class="text-danger">{{ countdownObject.minutes < 10 ? '0' : '' }}{{ countdownObject.minutes }}</strong>
                    <span>{{ t('minutes') }}</span>
                  </div>
                  <div class="item bg-white c-p-1 c-rounded-sm flex c-flex-col c-items-center c-mr-1">
                    <strong class="text-danger">{{ countdownObject.seconds < 10 ? '0' : '' }}{{ countdownObject.seconds }}</strong>
                    <span>{{ t('seconds') }}</span>
                  </div>
                </div>
                <p
                  v-if="notice"
                  class="c-m-0 font-bold c-lg:col-auto col-12 c-p-0 text-left"
                  v-html="notice.text"
                />
              </div>
            </div>
            <!-- close button -->
            <div class="col-auto c-pl-2 z-10 c-xl:c-mt-0 c-xl:c-mr-0 mr-n2 mt-n2">
              <button
                type="button"
                class="btn c-p-1 c-rounded-pill text-white"
                :aria-expanded="isVisible"
                aria-controls="notice-very-top"
                aria-label="Close notice"
                @click="isVisible = !isVisible"
              >
                <i class="mi-times icon-20" />
              </button>
            </div>
            <a
              v-if="notice.link"
              :href="notice.link"
              class="stretched-link"
            />
          </div>
        </div>
      </div>
    </div>
  </TransitionCollapse>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import RetailApiV2 from '@/api/retailApi/RetailApiV2'
import TransitionCollapse from '@/components/general/BaseTransitionCollapse.vue'

const { t } = useI18n()
const route = useRoute()

const isVisible = ref(true)
const notices = ref([])
const noticeIndex = ref()
const countdownObject = ref();
const notice = computed(() => {
  return notices.value.length >= noticeIndex.value ? notices.value[noticeIndex.value] : []
})

const intervalId = ref(null)

const updateNotice = () => {
  if (intervalId.value) clearInterval(intervalId.value)
  if (notices.value.length > 0) {
    noticeIndex.value = Math.floor(notices.value.length * Math.random())
    const targetTime = new Date(notice.value.validTo).getTime()
    setCountdown(targetTime, (time) => { countdownObject.value = time })
    intervalId.value = startCountdown(notice.value.validTo, (time) => { countdownObject.value = time })
  }
}
const setCountdown = (targetTime, callback) => {
  const now = Date.now();
  const distance = targetTime - now;

  if (distance <= 0) {
    callback({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    });
    return;
  }

  const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((distance % (1000 * 60)) / 1000);

  callback({ days, hours, minutes, seconds });
}

const startCountdown = (targetDate, callback) => {
  const targetTime = new Date(targetDate).getTime();

  const intervalId = setInterval(() => {
    if (Date.now() > targetTime) {
      clearInterval(intervalId)
      return
    }
    setCountdown(targetTime, callback)
  }, 1000);

  return intervalId;
}

onMounted(async () => {
  const { data } = await RetailApiV2.get('notices?severity=info&limit=6')

  for (const item of data.notices) {
    item.text = item.text.replaceAll(/<p[^>]*>/gu, '')?.replace(/<\/?p[^>]*>/gu, '')
    notices.value.push(item)
  }
  updateNotice()
})

onUnmounted(() => { if (intervalId.value) clearInterval(intervalId.value) })

watch(
  () => route.fullPath,
  () => {
    updateNotice()
  },
)
</script>

<i18n>
  {
    "cs": {
    "days": "Dny",
    "hours": "Hodiny",
    "minutes": "Minuty",
    "seconds": "Sekundy"
},
    "sk": {
    "days": "Dni",
    "hours": "Hodiny",
    "minutes": "Minúty",
    "seconds": "Sekundy"
},
    "pl": {
    "days": "Dni",
    "hours": "Godzin",
    "minutes": "Minut",
    "seconds": "Sekund"
},
    "hu": {
    "days": "Nap",
    "hours": "Óra",
    "minutes": "Perc",
    "seconds": "Másodperc"
},
    "de": {
    "days": "Tage",
    "hours": "Stunden",
    "minutes": "Minuten",
    "seconds": "Sekunden"
},
    "at": {
    "days": "Tage",
    "hours": "Stunden",
    "minutes": "Minuten",
    "seconds": "Sekunden"
},
    "ro": {
    "days": "Zile",
    "hours": "Ore",
    "minutes": "Minute",
    "seconds": "Secunde"
},
    "fr": {
    "days": "Jours",
    "hours": "Heures",
    "minutes": "Minutes",
    "seconds": "Secondes"
},
    "it": {
    "days": "Giorni",
    "hours": "Ore",
    "minutes": "Minuti",
    "seconds": "Secondi"
}
  }
</i18n>

<style lang="scss" scoped>
.countdown {
  .item {
    min-width: 40px;
    strong {font-size: 16px;}
    span {font-size: 10px;}

    @media (min-width: 1023px) {
      min-width: 80px;

      strong {font-size: 20px;}
      span {font-size: 12px;}
    }
  }
}
#notice-very-top {
  z-index: 1000;
  min-height: 55px;

  p {
    display: block;
    overflow-y: hidden;
    font-size: 0.875rem;
  }
}

.notice-on-top {
  &.red {
    background: hsla(4, 84%, 49%, 1);
  }

  &.green {
    background: rgb(55, 123, 73);
  }

  &.yellow {
    background: hsla(50, 57%, 47%, 1);
  }

  &.olive_green {
    background: hsla(64, 73%, 16%, 1);
  }

  &.brown {
    background: hsla(26, 58%, 23%, 1);

    a {
      color: $primary-color-450;
    }
  }

  &.dark_brown {
    background: hsla(23, 41%, 9%, 1);
  }

  &.orange {
    background: hsla(39, 100%, 77%, 1);
  }

  &.dark_orange {
    background: hsla(14, 93%, 53%, 1);
  }

  &.purple {
    background: hsla(279, 95%, 49%, 1);
  }

  &.blue {
    background: hsla(214, 92%, 47%, 1);
  }

  &.beige {
    background: hsla(54, 50%, 28%, 1);
  }

  &.bordeaux_red {
    background: hsla(296, 100%, 6%, 1);
  }

  &.ochre {
    background: hsla(30, 78%, 47%, 1);
  }
}
</style>
